import React, { useState, useEffect } from "react";
import API from "@/lib/API";
import { orderingQuotationService } from "../../ordering/services/orderingQuotationService";
import { budgetQuotationService } from "../../budget/services/budgetQuotationService";
import { Receipt, Package, CheckSquare, IndianRupee } from "lucide-react";

export default function ApprovalsStatsCards() {
  const [stats, setStats] = useState({
    expenses: 0,
    ordering: 0,
    quotation: 0,
    materials: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchStats = async () => {
      setLoading(true);
      try {
        const [
          expensesRes,
          orderingRes,
          budgetRes,
          materialsRes
        ] = await Promise.allSettled([
          API.get("/general-expenses"),
          orderingQuotationService.getQuotations(),
          budgetQuotationService.getQuotations(),
          API.get("/final-inventory/material-requests?ownerView=true")
        ]);

        if (!isMounted) return;

        let expensesCount = 0;
        let orderingCount = 0;
        let quotationCount = 0;
        let materialsCount = 0;

        // Expenses
        if (expensesRes.status === "fulfilled") {
          const data = expensesRes.value.data?.data || expensesRes.value.data || expensesRes.value || [];
          expensesCount = data.filter(e => String(e.status).toLowerCase() === "pending").length;
        }

        // Ordering (Appliances/Stone/Transport)
        if (orderingRes.status === "fulfilled") {
          const data = orderingRes.value?.data || orderingRes.value || [];
          if (Array.isArray(data)) {
            orderingCount = data.filter(q => String(q.status).toLowerCase() === "pending").length;
          }
        }

        // Quotation (Budget)
        if (budgetRes.status === "fulfilled") {
          const data = budgetRes.value?.data || budgetRes.value || [];
          if (Array.isArray(data)) {
            quotationCount = data.filter(q => String(q.status).toLowerCase() === "pending").length;
          }
        }

        // Materials
        if (materialsRes.status === "fulfilled") {
          const data = materialsRes.value.data?.data || materialsRes.value.data || materialsRes.value || [];
          materialsCount = data.filter(m => String(m.status).toLowerCase() === "pending").length;
        }

        setStats({
          expenses: expensesCount,
          ordering: orderingCount,
          quotation: quotationCount,
          materials: materialsCount
        });
      } catch (err) {
        console.error("Failed to fetch approval stats:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchStats();

    return () => {
      isMounted = false;
    };
  }, []);

  const statCards = [
    { label: "Expenses", count: stats.expenses, icon: Receipt, color: "text-blue-500", bg: "bg-blue-50" },
    { label: "Ordering", count: stats.ordering, icon: CheckSquare, color: "text-indigo-500", bg: "bg-indigo-50" },
    { label: "Quotation", count: stats.quotation, icon: IndianRupee, color: "text-emerald-500", bg: "bg-emerald-50" },
    { label: "Materials", count: stats.materials, icon: Package, color: "text-amber-500", bg: "bg-amber-50" }
  ];

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 w-full">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 h-24 animate-pulse flex items-center gap-4">
            <div className="w-12 h-12 bg-slate-100 rounded-full"></div>
            <div className="flex flex-col gap-2 flex-1">
              <div className="h-4 bg-slate-100 rounded w-1/2"></div>
              <div className="h-6 bg-slate-100 rounded w-1/4"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 w-full">
      {statCards.map((card, idx) => (
        <div key={idx} className="bg-white rounded-xl shadow-sm border border-slate-200/60 p-5 flex items-center justify-between hover:shadow-md transition-shadow duration-200">
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium text-slate-500">{card.label}</p>
            <div className="flex items-end gap-2">
              <h3 className="text-3xl font-bold text-slate-800 tracking-tight">{card.count}</h3>
              <span className="text-[11px] font-medium text-slate-400 mb-1.5 uppercase tracking-wide">Pending</span>
            </div>
          </div>
          <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${card.bg} ${card.color}`}>
            <card.icon className="w-6 h-6" strokeWidth={2} />
          </div>
        </div>
      ))}
    </div>
  );
}
